// Document templates for downloadable resources - PDF Generation
import { jsPDF } from 'jspdf';

// Helper function to add styled text
const addTitle = (doc, text, y) => {
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(text, 105, y, { align: 'center' });
  return y + 8;
};

const addSubtitle = (doc, text, y) => {
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100);
  doc.text(text, 105, y, { align: 'center' });
  doc.setTextColor(0);
  return y + 6;
};

const addSectionHeader = (doc, text, y) => {
  doc.setFillColor(16, 185, 129); // emerald-500
  doc.rect(15, y - 5, 180, 8, 'F');
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255);
  doc.text(text, 20, y);
  doc.setTextColor(0);
  return y + 10;
};

const addCheckboxLine = (doc, text, y, checked = false) => {
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  // Draw checkbox
  doc.rect(20, y - 3, 4, 4);
  if (checked) {
    doc.text('✓', 20.5, y);
  }
  doc.text(text, 28, y);
  return y + 6;
};

const addLine = (doc, text, y, indent = 20) => {
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const lines = doc.splitTextToSize(text, 170);
  doc.text(lines, indent, y);
  return y + (lines.length * 5);
};

const addInputLine = (doc, label, y, lineWidth = 80) => {
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(label, 20, y);
  const labelWidth = doc.getTextWidth(label);
  doc.line(20 + labelWidth + 2, y, 20 + labelWidth + 2 + lineWidth, y);
  return y + 8;
};

const addFooter = (doc) => {
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(128);
    doc.text('WSafeTraining.com - Free OSHA Training | powered by wsafe.ai - patent pending ai technology', 105, 290, { align: 'center' });
    doc.text(`Page ${i} of ${pageCount}`, 195, 290, { align: 'right' });
    doc.setTextColor(0);
  }
};

const checkPageBreak = (doc, y, needed = 30) => {
  if (y > 270 - needed) {
    doc.addPage();
    return 20;
  }
  return y;
};

// Safety Inspection Checklist PDF
export const generateSafetyInspectionChecklist = () => {
  const doc = new jsPDF();
  let y = 20;

  // Header
  y = addTitle(doc, 'WORKPLACE SAFETY INSPECTION CHECKLIST', y);
  y = addSubtitle(doc, 'WSafeTraining.com | powered by wsafe.ai - patent pending ai technology', y);
  y += 5;

  // Info fields
  y = addInputLine(doc, 'Date:', y, 50);
  y = addInputLine(doc, 'Inspector:', y, 70);
  y = addInputLine(doc, 'Location/Area:', y, 60);
  y = addInputLine(doc, 'Department:', y, 60);
  y += 5;

  // Instructions
  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  doc.text('Instructions: Check (✓) if satisfactory, (X) if needs attention, N/A if not applicable', 20, y);
  y += 10;

  // Section 1: General Housekeeping
  y = addSectionHeader(doc, 'SECTION 1: GENERAL HOUSEKEEPING', y);
  y = addCheckboxLine(doc, 'Floors clean and free of debris, spills, and tripping hazards', y);
  y = addCheckboxLine(doc, 'Aisles and walkways clear and unobstructed (minimum 28" width)', y);
  y = addCheckboxLine(doc, 'Storage areas organized and materials properly stacked', y);
  y = addCheckboxLine(doc, 'Trash and waste disposed of properly', y);
  y = addCheckboxLine(doc, 'Work areas free of unnecessary items', y);
  y = addCheckboxLine(doc, 'Lighting adequate for tasks performed', y);
  y += 3;
  y = addInputLine(doc, 'Notes:', y, 140);
  y += 5;

  // Section 2: Fire Safety
  y = checkPageBreak(doc, y, 60);
  y = addSectionHeader(doc, 'SECTION 2: FIRE SAFETY', y);
  y = addCheckboxLine(doc, 'Fire extinguishers accessible and properly mounted', y);
  y = addCheckboxLine(doc, 'Fire extinguishers inspected within last month (check tag)', y);
  y = addCheckboxLine(doc, 'Emergency exits clearly marked and unobstructed', y);
  y = addCheckboxLine(doc, 'Exit signs illuminated and visible', y);
  y = addCheckboxLine(doc, 'Flammable materials stored properly in approved containers', y);
  y = addCheckboxLine(doc, 'Electrical panels have 36" clearance', y);
  y = addCheckboxLine(doc, 'No overloaded electrical outlets', y);
  y = addCheckboxLine(doc, 'Extension cords in good condition (not permanent wiring)', y);
  y += 3;
  y = addInputLine(doc, 'Notes:', y, 140);
  y += 5;

  // Section 3: Electrical Safety
  y = checkPageBreak(doc, y, 50);
  y = addSectionHeader(doc, 'SECTION 3: ELECTRICAL SAFETY', y);
  y = addCheckboxLine(doc, 'Electrical cords free of damage, fraying, or exposed wires', y);
  y = addCheckboxLine(doc, 'Ground fault circuit interrupters (GFCI) tested and working', y);
  y = addCheckboxLine(doc, 'Electrical panels properly labeled', y);
  y = addCheckboxLine(doc, 'Junction boxes covered', y);
  y = addCheckboxLine(doc, 'No electrical equipment near water sources', y);
  y = addCheckboxLine(doc, 'Lockout/Tagout procedures posted and followed', y);
  y += 3;
  y = addInputLine(doc, 'Notes:', y, 140);
  y += 5;

  // Section 4: PPE
  y = checkPageBreak(doc, y, 50);
  y = addSectionHeader(doc, 'SECTION 4: PERSONAL PROTECTIVE EQUIPMENT (PPE)', y);
  y = addCheckboxLine(doc, 'Required PPE available and accessible', y);
  y = addCheckboxLine(doc, 'PPE in good condition (no damage or excessive wear)', y);
  y = addCheckboxLine(doc, 'Employees wearing appropriate PPE for tasks', y);
  y = addCheckboxLine(doc, 'PPE storage areas clean and organized', y);
  y = addCheckboxLine(doc, 'Eye wash stations accessible and inspected', y);
  y = addCheckboxLine(doc, 'Safety data sheets (SDS) accessible for all chemicals', y);
  y += 3;
  y = addInputLine(doc, 'Notes:', y, 140);
  y += 5;

  // Section 5: Machinery & Equipment
  y = checkPageBreak(doc, y, 50);
  y = addSectionHeader(doc, 'SECTION 5: MACHINERY & EQUIPMENT', y);
  y = addCheckboxLine(doc, 'Machine guards in place and functioning', y);
  y = addCheckboxLine(doc, 'Emergency stop buttons accessible and tested', y);
  y = addCheckboxLine(doc, 'Equipment properly maintained (check logs)', y);
  y = addCheckboxLine(doc, 'Warning labels visible and legible', y);
  y = addCheckboxLine(doc, 'Proper tool storage', y);
  y = addCheckboxLine(doc, 'Ladders in good condition with proper labeling', y);
  y += 3;
  y = addInputLine(doc, 'Notes:', y, 140);
  y += 5;

  // Section 6: Emergency Preparedness
  y = checkPageBreak(doc, y, 50);
  y = addSectionHeader(doc, 'SECTION 6: EMERGENCY PREPAREDNESS', y);
  y = addCheckboxLine(doc, 'Emergency action plan posted', y);
  y = addCheckboxLine(doc, 'First aid kits stocked and accessible', y);
  y = addCheckboxLine(doc, 'Emergency contact numbers posted', y);
  y = addCheckboxLine(doc, 'Evacuation routes posted and clear', y);
  y = addCheckboxLine(doc, 'Emergency drills conducted as scheduled', y);
  y = addCheckboxLine(doc, 'AED (if applicable) inspected and accessible', y);
  y += 3;
  y = addInputLine(doc, 'Notes:', y, 140);
  y += 8;

  // Summary section
  y = checkPageBreak(doc, y, 60);
  y = addSectionHeader(doc, 'SUMMARY OF FINDINGS', y);
  y = addInputLine(doc, 'Total Items Inspected:', y, 30);
  y = addInputLine(doc, 'Satisfactory:', y, 30);
  y = addInputLine(doc, 'Needs Attention:', y, 30);
  y += 5;
  doc.setFont('helvetica', 'bold');
  doc.text('IMMEDIATE HAZARDS (Require same-day correction):', 20, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  y = addInputLine(doc, '1.', y, 150);
  y = addInputLine(doc, '2.', y, 150);
  y = addInputLine(doc, '3.', y, 150);
  y += 5;

  // Sign-off
  y = checkPageBreak(doc, y, 40);
  y = addSectionHeader(doc, 'SIGN-OFF', y);
  y = addInputLine(doc, 'Inspector Signature:', y, 80);
  y = addInputLine(doc, 'Supervisor Review:', y, 80);
  y = addInputLine(doc, 'Next Inspection Due:', y, 50);

  addFooter(doc);
  return doc;
};

// Job Hazard Analysis Template PDF
export const generateJobHazardAnalysisTemplate = () => {
  const doc = new jsPDF();
  let y = 20;

  // Header
  y = addTitle(doc, 'JOB HAZARD ANALYSIS (JHA)', y);
  y = addSubtitle(doc, 'WSafeTraining.com | powered by wsafe.ai - patent pending ai technology', y);
  y += 5;

  // Job Info
  y = addInputLine(doc, 'Job Title:', y, 120);
  y = addInputLine(doc, 'Department:', y, 60);
  y = addInputLine(doc, 'Date:', y, 40);
  y = addInputLine(doc, 'Analyst:', y, 60);
  y = addInputLine(doc, 'Reviewed By:', y, 60);
  y += 5;

  // PPE Section
  y = addSectionHeader(doc, 'REQUIRED PERSONAL PROTECTIVE EQUIPMENT (PPE)', y);
  
  // PPE Grid - 3 columns
  const ppeItems = [
    'Safety Glasses', 'Face Shield', 'Hard Hat',
    'Hearing Protection', 'Safety Shoes', 'Steel-Toed Boots',
    'Gloves', 'Respirator', 'Fall Protection',
    'Chemical Apron', 'High-Vis Vest', 'Other:'
  ];
  
  let col = 0;
  let startY = y;
  ppeItems.forEach((item, i) => {
    const xPos = 20 + (col * 60);
    doc.rect(xPos, y - 3, 4, 4);
    doc.setFontSize(9);
    doc.text(item, xPos + 6, y);
    col++;
    if (col >= 3) {
      col = 0;
      y += 7;
    }
  });
  y += 10;

  // Training Section
  y = addSectionHeader(doc, 'REQUIRED TRAINING', y);
  const trainingItems = [
    'Hazard Communication', 'Lockout/Tagout', 'Confined Space',
    'Fall Protection', 'Forklift Operation', 'First Aid/CPR',
    'Fire Extinguisher', 'Bloodborne Pathogens', 'Electrical Safety'
  ];
  
  col = 0;
  trainingItems.forEach((item) => {
    const xPos = 20 + (col * 60);
    doc.rect(xPos, y - 3, 4, 4);
    doc.setFontSize(9);
    doc.text(item, xPos + 6, y);
    col++;
    if (col >= 3) {
      col = 0;
      y += 7;
    }
  });
  y += 10;

  // JHA Steps
  for (let step = 1; step <= 4; step++) {
    y = checkPageBreak(doc, y, 50);
    y = addSectionHeader(doc, `STEP ${step}`, y);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Job Step Description:', 20, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.rect(20, y, 170, 15);
    y += 20;

    doc.setFont('helvetica', 'bold');
    doc.text('Potential Hazards:', 20, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.rect(20, y, 170, 15);
    y += 20;

    doc.setFont('helvetica', 'bold');
    doc.text('Recommended Safe Procedures:', 20, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.rect(20, y, 170, 15);
    y += 20;
  }

  // Hazard Categories Reference
  y = checkPageBreak(doc, y, 60);
  y = addSectionHeader(doc, 'HAZARD CATEGORIES REFERENCE', y);
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('STRUCK BY/AGAINST:', 20, y);
  doc.text('CAUGHT IN/BETWEEN:', 80, y);
  doc.text('FALL HAZARDS:', 140, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  doc.text('• Moving vehicles', 20, y);
  doc.text('• Machinery', 80, y);
  doc.text('• From heights', 140, y);
  y += 4;
  doc.text('• Falling objects', 20, y);
  doc.text('• Equipment', 80, y);
  doc.text('• Same level', 140, y);
  y += 4;
  doc.text('• Flying particles', 20, y);
  doc.text('• Pinch points', 80, y);
  doc.text('• Into openings', 140, y);
  y += 10;

  // Signatures
  y = checkPageBreak(doc, y, 50);
  y = addSectionHeader(doc, 'APPROVAL SIGNATURES', y);
  y = addInputLine(doc, 'Prepared By:', y, 80);
  y = addInputLine(doc, 'Supervisor:', y, 80);
  y = addInputLine(doc, 'Safety Manager:', y, 80);
  y += 5;

  // Employee Acknowledgment
  y = addSectionHeader(doc, 'EMPLOYEE ACKNOWLEDGMENT', y);
  doc.setFontSize(9);
  doc.text('I have reviewed this JHA and understand the hazards and safe work procedures.', 20, y);
  y += 8;

  // Table header
  doc.setFont('helvetica', 'bold');
  doc.text('Name (Print)', 20, y);
  doc.text('Signature', 80, y);
  doc.text('Date', 160, y);
  y += 3;
  doc.line(20, y, 190, y);
  y += 5;
  
  // Empty rows for signatures
  for (let i = 0; i < 5; i++) {
    doc.line(20, y + 5, 75, y + 5);
    doc.line(80, y + 5, 155, y + 5);
    doc.line(160, y + 5, 190, y + 5);
    y += 10;
  }

  addFooter(doc);
  return doc;
};

// Emergency Action Plan Template PDF
export const generateEmergencyActionPlanTemplate = () => {
  const doc = new jsPDF();
  let y = 20;

  // Header
  y = addTitle(doc, 'EMERGENCY ACTION PLAN (EAP)', y);
  y = addSubtitle(doc, 'WSafeTraining.com | powered by wsafe.ai - patent pending ai technology', y);
  y += 5;

  // Company Info
  y = addInputLine(doc, 'Company Name:', y, 120);
  y = addInputLine(doc, 'Address:', y, 130);
  y = addInputLine(doc, 'Effective Date:', y, 50);
  y = addInputLine(doc, 'Plan Administrator:', y, 80);
  y += 5;

  // Purpose
  y = addSectionHeader(doc, '1. PURPOSE AND SCOPE', y);
  const purposeText = 'This Emergency Action Plan (EAP) establishes procedures for reporting emergencies, evacuation and shelter-in-place, employee accountability, and emergency response coordination. This plan applies to all employees, contractors, and visitors at this location.';
  y = addLine(doc, purposeText, y);
  y += 5;

  // Emergency Contacts
  y = checkPageBreak(doc, y, 70);
  y = addSectionHeader(doc, '2. EMERGENCY CONTACTS', y);
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(220, 38, 38); // Red
  doc.text('EMERGENCY: 911', 105, y, { align: 'center' });
  doc.setTextColor(0);
  y += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Internal Contacts:', 20, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  y = addInputLine(doc, 'Plan Administrator:', y, 80);
  y = addInputLine(doc, 'Backup Administrator:', y, 80);
  y = addInputLine(doc, 'Facility Manager:', y, 80);
  y = addInputLine(doc, 'Safety Manager:', y, 80);
  y += 3;

  doc.setFont('helvetica', 'bold');
  doc.text('External Contacts:', 20, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  y = addInputLine(doc, 'Fire Dept (non-emergency):', y, 70);
  y = addInputLine(doc, 'Police (non-emergency):', y, 70);
  doc.text('Poison Control: 1-800-222-1222', 20, y);
  y += 6;
  y = addInputLine(doc, 'Hospital:', y, 80);
  y += 5;

  // Evacuation Procedures
  y = checkPageBreak(doc, y, 80);
  y = addSectionHeader(doc, '3. EVACUATION PROCEDURES', y);
  
  doc.setFont('helvetica', 'bold');
  doc.text('WHEN TO EVACUATE:', 20, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  const evacuateReasons = ['Fire alarm sounds', 'Announced evacuation', 'Smell of smoke or gas', 'Structural damage'];
  evacuateReasons.forEach(reason => {
    doc.text(`• ${reason}`, 25, y);
    y += 5;
  });
  y += 3;

  doc.setFont('helvetica', 'bold');
  doc.text('EVACUATION STEPS:', 20, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  const steps = [
    '1. Stop work immediately',
    '2. Leave belongings behind',
    '3. Close doors behind you (do not lock)',
    '4. Use nearest safe exit (DO NOT use elevators)',
    '5. Assist visitors and persons with disabilities',
    '6. Report to designated assembly area',
    '7. Report to supervisor for accountability'
  ];
  steps.forEach(step => {
    doc.text(step, 25, y);
    y += 5;
  });
  y += 5;

  // Assembly Areas
  y = checkPageBreak(doc, y, 40);
  y = addSectionHeader(doc, '4. ASSEMBLY AREAS', y);
  y = addInputLine(doc, 'Primary Assembly Area:', y, 100);
  y = addInputLine(doc, 'Secondary Assembly Area:', y, 100);
  y = addInputLine(doc, 'Assembly Area Manager:', y, 80);
  y += 5;

  // Fire Emergency
  y = checkPageBreak(doc, y, 70);
  y = addSectionHeader(doc, '5. FIRE EMERGENCY PROCEDURES', y);
  
  doc.setFont('helvetica', 'bold');
  doc.text('IF YOU DISCOVER A FIRE:', 20, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  const fireSteps = [
    '1. Activate nearest fire alarm pull station',
    '2. Call 911',
    '3. Alert others in immediate area',
    '4. Evacuate building using nearest exit',
    '5. Close doors to contain fire',
    '6. DO NOT attempt to fight large fires'
  ];
  fireSteps.forEach(step => {
    doc.text(step, 25, y);
    y += 5;
  });
  y += 3;

  // PASS method
  doc.setFont('helvetica', 'bold');
  doc.text('FIRE EXTINGUISHER USE (P.A.S.S.):', 20, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  doc.text('P - Pull the pin', 25, y); y += 5;
  doc.text('A - Aim at base of fire', 25, y); y += 5;
  doc.text('S - Squeeze the handle', 25, y); y += 5;
  doc.text('S - Sweep side to side', 25, y); y += 8;

  // Medical Emergency
  y = checkPageBreak(doc, y, 50);
  y = addSectionHeader(doc, '6. MEDICAL EMERGENCY PROCEDURES', y);
  const medicalSteps = [
    '1. Call 911 immediately for serious injury/illness',
    '2. Do not move injured person unless in danger',
    '3. Administer first aid if trained',
    '4. Send someone to guide emergency responders',
    '5. Notify Plan Administrator'
  ];
  medicalSteps.forEach(step => {
    doc.text(step, 25, y);
    y += 5;
  });
  y += 3;
  y = addInputLine(doc, 'First Aid Kit Locations:', y, 100);
  y += 5;

  // Active Threat
  y = checkPageBreak(doc, y, 60);
  y = addSectionHeader(doc, '7. ACTIVE THREAT PROCEDURES', y);
  
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(220, 38, 38);
  doc.text('RUN - HIDE - FIGHT', 105, y, { align: 'center' });
  doc.setTextColor(0);
  doc.setFontSize(10);
  y += 8;

  doc.setFont('helvetica', 'bold');
  doc.text('RUN (if safe path exists):', 20, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  doc.text('• Leave belongings  • Prevent others from entering  • Call 911 when safe', 25, y);
  y += 8;

  doc.setFont('helvetica', 'bold');
  doc.text('HIDE (if escape not possible):', 20, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  doc.text('• Lock/barricade doors  • Hide behind large objects  • Silence phones  • Stay quiet', 25, y);
  y += 8;

  doc.setFont('helvetica', 'bold');
  doc.text('FIGHT (last resort only):', 20, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  doc.text('• Act aggressively  • Use improvised weapons  • Commit to your actions', 25, y);
  y += 10;

  // Sign-off
  y = checkPageBreak(doc, y, 50);
  y = addSectionHeader(doc, '8. ACKNOWLEDGMENT', y);
  doc.text('I have received, read, and understand this Emergency Action Plan.', 20, y);
  y += 8;

  // Table header
  doc.setFont('helvetica', 'bold');
  doc.text('Name (Print)', 20, y);
  doc.text('Signature', 80, y);
  doc.text('Date', 160, y);
  y += 3;
  doc.line(20, y, 190, y);
  y += 5;
  
  // Empty rows for signatures
  for (let i = 0; i < 5; i++) {
    doc.line(20, y + 5, 75, y + 5);
    doc.line(80, y + 5, 155, y + 5);
    doc.line(160, y + 5, 190, y + 5);
    y += 10;
  }

  addFooter(doc);
  return doc;
};

// Function to trigger PDF download
export const downloadDocument = (title, generator) => {
  const doc = generator();
  const filename = title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  doc.save(`${filename}.pdf`);
};
